import { isFlowTheme } from '../utils'
import { getInitialContentVersionAndMarkElementsAsNew } from './utils'

/**
 * Sidebar or bottom bar elements rendering features if clicked on
 */
export enum FeatureElements {
  OverviewSideBarElement = 'OverviewSideBarElement',
  CompanyAdministrationSideBarElement = 'CompanyAdministrationSideBarElement',
  DashboardSideBarElement = 'DashboardSideBarElement',
  PropertiesSideBarElement = 'PropertiesSideBarElement',
  PropertySolarSideBarElement = 'PropertySolarSideBarElement',
  PropertyBatterySideBarElement = 'PropertyBatterySideBarElement',
  PropertyPowermeterSideBarElement = 'PropertyPowermeterSideBarElement',
  PropertyWallboxSideBarElement = 'PropertyWallboxSideBarElement',
  PropertySmartFunctionSideBarElement = 'PropertySmartFunctionSideBarElement',
  PropertySmartFunctionsDynamicEmsFeature = 'PropertySmartFunctionsDynamicEmsFeature',
  PropertyTariffsSideBarElement = 'PropertyTariffsSideBarElement',
  PropertyEmergencyPowerSideBarElement = 'PropertyEmergencyPowerSideBarElement',
  PropertyInstallationSideBarElement = 'PropertyInstallationSideBarElement',
  PropertyEndUserSettingsSolarElement = 'PropertyEndUserSettingsSolarElement',
  PropertyEndUserSettingsBadgesElement = 'PropertyEndUserSettingsBadgesElement',
  PropertyEndUserSettingsStorageEnergyManagementElement = 'PropertyEndUserSettingsStorageEnergyManagementElement',
  PropertyEndUserSettingsWallboxElement = 'PropertyEndUserSettingsWallboxElement',
  PropertyEndUserSettingsBoilerElement = 'PropertyEndUserSettingsBoilerElement',
  PropertyStorageBatterySettingsElement = 'PropertyStorageBatterySettingsElement',
  PropertyGridAndSystemProtectionConfigurationSideBarElement = 'PropertyGridAndSystemProtectionConfigurationSideBarElement',
  PropertyAdvancedSettingsSideBarElement = 'PropertyAdvancedSettingsSideBarElement',
  PropertyEndUserSettingsSideBarElement = 'PropertyEndUserSettingsSideBarElement',
  EMobilitySideBarElement = 'EMobilitySideBarElement',
  ExtensionSideBarElement = 'ExtensionSideBarElement',
  TibberExtensionElement = 'TibberExtensionElement',
  LuoxDynamicTariffExtensionElement = 'LuoxDynamicTariffExtensionElement',
  ReferFriendsSideBarElement = 'ReferFriendsSideBarElement',
  ContactSideBarElement = 'ContactSideBarElement',
  MessageOfTheDaySideBarElement = 'MessageOfTheDaySideBarElement',
  ShopSideBarElement = 'ShopSideBarElement',
  RetrofitSidebarElement = 'RetrofitSidebarElement',
  ReonicConfiguratorSidebarElement = 'ReonicConfiguratorSidebarElement',
  VehicleIntegrationSideBarElement = 'VehicleIntegrationSideBarElement',
  DocumentsSidebarElement = 'DocumentsSidebarElement',
  DocumentLinksSidebarElement = 'DocumentLinksSidebarElement',
  SmartHomeSidebarElement = 'SmartHomeSidebarElement',
  AI360SidebarElement = 'AI360SidebarElement',
  InstallationConfigurationSideBarElement = 'InstallationConfigurationSideBarElement',
}

export type FeatureContentElementKey = `${FeatureElements}`

export type FeatureContentVersionType = {
  [key in FeatureContentElementKey]: number
}

/**
 * Elements which function as the header of multiple other elements. If clicked then new-label should not disappear until also the child element has been clicked.
 */
export const PARENT_FEATURE_ELEMENTS: FeatureContentElementKey[] = [FeatureElements.PropertiesSideBarElement]

/**
 * This objects contains elements (mostly on side bar) which can be clicked to view content. Each element has a version indicating if new content is added.
 * Incrementing the version adds a "new" label to the element if the user already has stored a feature content version in the DB (for new users update INITIAL_FEATURE_CONTENT_VERSION)
 */
export const FeatureContentVersions: FeatureContentVersionType = {
  OverviewSideBarElement: 1,
  CompanyAdministrationSideBarElement: 1,
  DashboardSideBarElement: 1,
  PropertiesSideBarElement: 1,
  PropertySolarSideBarElement: 1,
  PropertyBatterySideBarElement: 1,
  PropertyPowermeterSideBarElement: 1,
  PropertyWallboxSideBarElement: 1,
  PropertySmartFunctionSideBarElement: 2,
  PropertySmartFunctionsDynamicEmsFeature: 1,
  PropertyTariffsSideBarElement: 0,
  PropertyEmergencyPowerSideBarElement: 1,
  PropertyInstallationSideBarElement: 1,
  PropertyGridAndSystemProtectionConfigurationSideBarElement: 1,
  PropertyAdvancedSettingsSideBarElement: 0,
  PropertyEndUserSettingsSideBarElement: 0,
  PropertyEndUserSettingsSolarElement: 0,
  PropertyEndUserSettingsBadgesElement: 0,
  PropertyEndUserSettingsStorageEnergyManagementElement: 0,
  PropertyEndUserSettingsWallboxElement: 0,
  PropertyEndUserSettingsBoilerElement: 0,
  PropertyStorageBatterySettingsElement: 0,
  EMobilitySideBarElement: 1,
  ExtensionSideBarElement: isFlowTheme ? 2 : 1,
  TibberExtensionElement: 2,
  LuoxDynamicTariffExtensionElement: 2,
  ReferFriendsSideBarElement: 1,
  ContactSideBarElement: 1,
  MessageOfTheDaySideBarElement: 1,
  ShopSideBarElement: 1,
  RetrofitSidebarElement: 1,
  ReonicConfiguratorSidebarElement: 1,
  VehicleIntegrationSideBarElement: 2,
  DocumentsSidebarElement: 1,
  DocumentLinksSidebarElement: 0,
  SmartHomeSidebarElement: 1,
  InstallationConfigurationSideBarElement: 1,
  AI360SidebarElement: 1,
}

/**
 * The initial feature content version saved in the database for new users.
 * ElementKeys listed here will be shown with a "new" label to those users (for users who already have stored content versions in the database the FeatureContentVersions object above is relevant)
 */
export const INITIAL_FEATURE_CONTENT_VERSION: FeatureContentVersionType = getInitialContentVersionAndMarkElementsAsNew([
  'ReferFriendsSideBarElement',
  'ExtensionSideBarElement',
  'RetrofitSidebarElement',
  'TibberExtensionElement',
])
